import React, { useMemo } from "react";
import QRCode from "qrcode";

const buildQrCodeSvgPath = ({
  value = "",
  errorCorrectionLevel = "H",
  margin = 4,
} = {}) => {
  const qr = QRCode.create(String(value || ""), {
    errorCorrectionLevel,
  });
  const moduleSize = Number(qr?.modules?.size || 0);
  const modules = qr?.modules?.data || [];
  if (!moduleSize || !modules.length) {
    throw new Error("Could not create QR code.");
  }

  const quietZone = Math.max(0, Number(margin) || 0);
  const rects = [];
  for (let y = 0; y < moduleSize; y += 1) {
    for (let x = 0; x < moduleSize; x += 1) {
      if (modules[y * moduleSize + x]) {
        rects.push(`M${x + quietZone},${y + quietZone}h1v1h-1z`);
      }
    }
  }

  return {
    path: rects.join(""),
    viewBox: `0 0 ${moduleSize + quietZone * 2} ${moduleSize + quietZone * 2}`,
    moduleCount: moduleSize,
    margin: quietZone,
    errorCorrectionLevel,
  };
};

export default function QrCodeSvg({
  value = "",
  size = 220,
  foreground = "#000000",
  background = "#ffffff",
  label = "QR code",
  margin = 4,
  errorCorrectionLevel = "H",
}) {
  const qr = useMemo(() => {
    try {
      return buildQrCodeSvgPath({ value, errorCorrectionLevel, margin });
    } catch (error) {
      return {
        error: error?.message || "Could not create QR code.",
        path: "",
        viewBox: "0 0 1 1",
      };
    }
  }, [errorCorrectionLevel, margin, value]);

  if (qr.error) {
    return (
      <div
        role="alert"
        style={{
          width: size,
          minHeight: size,
          display: "grid",
          placeItems: "center",
          padding: 16,
          borderRadius: 18,
          background,
          color: foreground,
          textAlign: "center",
          fontSize: 13,
          fontWeight: 800,
        }}
      >
        {qr.error}
      </div>
    );
  }

  return (
    <svg
      role="img"
      aria-label={label}
      width={size}
      height={size}
      viewBox={qr.viewBox}
      shapeRendering="crispEdges"
      style={{
        display: "block",
        width: size,
        height: size,
        maxWidth: "100%",
        borderRadius: 18,
        background,
      }}
    >
      <rect width="100%" height="100%" fill={background} />
      <path d={qr.path} fill={foreground} />
    </svg>
  );
}
